import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

export type LogLevel = 'info' | 'warning' | 'error';

interface ClientLogEvent {
  level: LogLevel;
  event_type: string;
  message?: string;
  action?: string;
  route?: string;
  http_status?: number;
  duration_ms?: number;
  detail?: Record<string, unknown>;
  client_ts: string;
}

/**
 * Buffers client-side errors/warnings and ships them to the backend ingest
 * endpoint without blocking the UI. Captures:
 *  - uncaught errors (via the global ErrorHandler, with stack traces)
 *  - console.error / console.warn calls (incl. framework warnings)
 *
 * Events are batched and flushed on an interval, when the buffer fills, and on
 * page hide (via sendBeacon so the tail is not lost). The owned `sessionId` is
 * also sent as the `X-Session-Id` header on every request so server-logged
 * rows correlate into a session.
 */
@Injectable({ providedIn: 'root' })
export class LoggingService {
  private readonly http = inject(HttpClient);

  private readonly endpoint = '/api/logs/';
  private readonly sessionKey = 'session_id';
  private readonly userKey = 'user';
  private readonly maxBuffer = 25;
  private readonly maxMessageLength = 2000;
  private readonly flushIntervalMs = 5000;
  private readonly dedupeWindowMs = 1000;

  private buffer: ClientLogEvent[] = [];
  // Captured before patching so internal logging never re-captures itself.
  private readonly originalConsoleError: (...args: unknown[]) => void =
    console.error.bind(console);
  private readonly originalConsoleWarn: (...args: unknown[]) => void =
    console.warn.bind(console);
  private lastSignature = '';
  private lastSignatureAt = 0;
  private installed = false;

  readonly sessionId: string = this.resolveSessionId();

  constructor() {
    this.install();
  }

  /** Log an uncaught error coming from the global ErrorHandler. */
  captureError(error: unknown): void {
    const { message, stack } = this.describe(error);
    this.enqueue({
      level: 'error',
      event_type: 'uncaught_error',
      message,
      detail: stack ? { stack: stack.slice(0, 8000) } : undefined,
    });
    // Keep dev visibility without re-capturing via the console patch.
    this.originalConsoleError(error);
  }

  private install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;

    console.error = (...args: unknown[]): void => {
      this.enqueue({
        level: 'error',
        event_type: 'console_error',
        message: this.stringify(args),
      });
      this.originalConsoleError(...args);
    };

    console.warn = (...args: unknown[]): void => {
      this.enqueue({
        level: 'warning',
        event_type: 'console_warning',
        message: this.stringify(args),
      });
      this.originalConsoleWarn(...args);
    };

    setInterval(() => this.flush(false), this.flushIntervalMs);

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush(true);
        }
      });
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flush(true));
    }
  }

  private enqueue(event: Omit<ClientLogEvent, 'client_ts'>): void {
    const message = event.message
      ? event.message.slice(0, this.maxMessageLength)
      : event.message;

    const signature = `${event.event_type}:${message ?? ''}`;
    const now = Date.now();
    if (
      signature === this.lastSignature &&
      now - this.lastSignatureAt < this.dedupeWindowMs
    ) {
      return;
    }
    this.lastSignature = signature;
    this.lastSignatureAt = now;

    this.buffer.push({ ...event, message, client_ts: new Date(now).toISOString() });
    if (this.buffer.length >= this.maxBuffer) {
      this.flush(false);
    }
  }

  private flush(useBeacon: boolean): void {
    if (this.buffer.length === 0) {
      return;
    }
    const events = this.buffer;
    this.buffer = [];

    const payload = {
      session_id: this.sessionId,
      user_id: this.currentUserId(),
      events,
    };

    if (
      useBeacon &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      try {
        const blob = new Blob([JSON.stringify(payload)], {
          type: 'application/json',
        });
        navigator.sendBeacon(this.endpoint, blob);
        return;
      } catch {
        // Fall through to a normal HTTP post.
      }
    }

    this.http.post(this.endpoint, payload).subscribe({ error: () => undefined });
  }

  private resolveSessionId(): string {
    try {
      const existing = sessionStorage.getItem(this.sessionKey);
      if (existing) {
        return existing;
      }
      const id = this.uuid();
      sessionStorage.setItem(this.sessionKey, id);
      return id;
    } catch {
      return this.uuid();
    }
  }

  private currentUserId(): number | null {
    try {
      const raw = localStorage.getItem(this.userKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { id?: number };
      return typeof parsed.id === 'number' ? parsed.id : null;
    } catch {
      return null;
    }
  }

  private describe(error: unknown): { message: string; stack?: string } {
    if (error instanceof Error) {
      return { message: `${error.name}: ${error.message}`, stack: error.stack };
    }
    if (typeof error === 'string') {
      return { message: error };
    }
    try {
      return { message: JSON.stringify(error) };
    } catch {
      return { message: String(error) };
    }
  }

  private stringify(args: unknown[]): string {
    return args
      .map(arg => {
        if (typeof arg === 'string') {
          return arg;
        }
        if (arg instanceof Error) {
          return `${arg.name}: ${arg.message}`;
        }
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
  }

  private uuid(): string {
    const cryptoObj = globalThis.crypto;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return cryptoObj.randomUUID();
    }
    return `sess-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  }
}
